import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  updateProfile,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth, googleProvider, hasFirebaseConfig } from '../lib/firebase';

function assertAuthConfigured() {
  if (!hasFirebaseConfig || !auth) {
    throw new Error('Firebase is not configured. Please set VITE_FIREBASE_* variables.');
  }
}

export function subscribeToAuthState(callback: (user: FirebaseUser | null) => void) {
  if (!hasFirebaseConfig || !auth) {
    callback(null);
    return () => undefined;
  }

  return onAuthStateChanged(auth, callback);
}

export async function signInWithEmail(email: string, password: string) {
  assertAuthConfigured();
  return signInWithEmailAndPassword(auth!, email, password);
}

export async function signUpWithEmail(email: string, password: string, displayName: string) {
  assertAuthConfigured();

  const userCredential = await createUserWithEmailAndPassword(auth!, email, password);
  const nextDisplayName = displayName.trim();

  if (nextDisplayName) {
    await updateProfile(userCredential.user, { displayName: nextDisplayName });
  }

  return userCredential;
}

export async function signInWithGooglePopup() {
  assertAuthConfigured();
  if (!googleProvider) {
    throw new Error('Google provider is not initialized.');
  }
  return signInWithPopup(auth!, googleProvider);
}

export async function requestPasswordReset(email: string) {
  assertAuthConfigured();
  return sendPasswordResetEmail(auth!, email);
}

export async function signOutCurrentUser() {
  if (!auth) return;
  await signOut(auth);
}

export async function changeCurrentUserPassword(currentPassword: string, newPassword: string) {
  assertAuthConfigured();

  const currentUser = auth?.currentUser;
  if (!currentUser || !currentUser.email) {
    throw new Error('No authenticated email/password user found.');
  }

  const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
  await reauthenticateWithCredential(currentUser, credential);
  await updatePassword(currentUser, newPassword);
}
